import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  RotateCcw, Plus, Search, Eye, X, Package,
  AlertCircle, CheckCircle, Clock, DollarSign
} from 'lucide-react';

interface ReturnItem {
  id: number;
  returnNumber?: string;
  returnId?: string;
  shipmentId?: number;
  shipmentTrackingNumber?: string;
  reason?: string;
  reasonDetails?: string;
  customerNotes?: string;
  adminNotes?: string;
  status?: string;
  refundStatus?: string;
  refundAmount?: number;
  refundMethod?: string;
  refundDate?: string;
  createdAt?: string;
  requestedAt?: string;
  approvedAt?: string;
  receivedAt?: string;
  completedAt?: string;
  merchantName?: string;
}

export default function ReturnsPage() {
  const [returns, setReturns] = useState<ReturnItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState<ReturnItem | null>(null);

  useEffect(() => { loadReturns(); }, []);

  const loadReturns = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/returns');
      if (response.data?.success !== false) {
        const payload = response.data?.data ?? response.data;
        const items = Array.isArray(payload) ? payload : (payload?.items ?? []);
        setReturns(Array.isArray(items) ? items : []);
      } else {
        setReturns([]);
      }
    } catch (error) {
      console.error('Error loading returns:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredReturns = Array.isArray(returns) ? returns.filter(r =>
    (r.returnNumber || r.returnId || '').toLowerCase().includes(search.toLowerCase()) ||
    String(r.shipmentTrackingNumber || r.shipmentId || '').toLowerCase().includes(search.toLowerCase()) ||
    (r.reason || '').toLowerCase().includes(search.toLowerCase()) ||
    (r.merchantName || '').toLowerCase().includes(search.toLowerCase())
  ) : [];

  const stats = {
    total: returns.length,
    pending: returns.filter(r => (r.status || '').toLowerCase() === 'pending').length,
    processing: returns.filter(r => (r.status || '').toLowerCase() === 'processing').length,
    completed: returns.filter(r => (r.status || '').toLowerCase() === 'completed').length,
    totalRefund: returns.reduce((sum, r) => sum + (r.refundAmount || 0), 0)
  };

  const getStatusStyle = (status: string) => {
    switch ((status || '').toLowerCase()) {
      case 'pending': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'processing': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'completed': return 'bg-green-50 text-green-700 border-green-200';
      case 'rejected': return 'bg-red-50 text-red-700 border-red-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch ((status || '').toLowerCase()) {
      case 'pending': return Clock;
      case 'processing': return Package;
      case 'completed': return CheckCircle;
      case 'rejected': return AlertCircle;
      default: return Clock;
    }
  };

  const getStatusLabel = (status: string) => {
    switch ((status || '').toLowerCase()) {
      case 'pending': return 'في الانتظار';
      case 'processing': return 'قيد المعالجة';
      case 'completed': return 'مكتمل';
      case 'rejected': return 'مرفوض';
      default: return status || '-';
    }
  };

  return (
    <div className='space-y-6' dir='rtl'>
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
        <div>
          <h1 className='text-3xl font-bold text-gray-900 flex items-center gap-3'>
            <div className='w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg'>
              <RotateCcw className='w-6 h-6 text-white' />
            </div>
            <div>
              <span>إدارة المرتجعات</span>
              <p className='text-sm text-gray-500 font-normal mt-1'>إدارة طلبات إرجاع الشحنات</p>
            </div>
          </h1>
        </div>
      </div>

      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4'>
        <Card className='border-0 shadow-lg hover:shadow-xl transition'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between mb-2'>
              <div className='w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center'>
                <RotateCcw className='w-5 h-5 text-orange-600' />
              </div>
            </div>
            <p className='text-sm text-gray-500'>إجمالي المرتجعات</p>
            <p className='text-2xl font-bold text-gray-900'>{stats.total}</p>
          </CardContent>
        </Card>

        <Card className='border-0 shadow-lg hover:shadow-xl transition'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between mb-2'>
              <div className='w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center'>
                <Clock className='w-5 h-5 text-yellow-600' />
              </div>
            </div>
            <p className='text-sm text-gray-500'>في الانتظار</p>
            <p className='text-2xl font-bold text-yellow-700'>{stats.pending}</p>
          </CardContent>
        </Card>

        <Card className='border-0 shadow-lg hover:shadow-xl transition'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between mb-2'>
              <div className='w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center'>
                <Package className='w-5 h-5 text-blue-600' />
              </div>
            </div>
            <p className='text-sm text-gray-500'>قيد المعالجة</p>
            <p className='text-2xl font-bold text-blue-700'>{stats.processing}</p>
          </CardContent>
        </Card>

        <Card className='border-0 shadow-lg hover:shadow-xl transition'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between mb-2'>
              <div className='w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center'>
                <CheckCircle className='w-5 h-5 text-green-600' />
              </div>
            </div>
            <p className='text-sm text-gray-500'>مكتمل</p>
            <p className='text-2xl font-bold text-green-700'>{stats.completed}</p>
          </CardContent>
        </Card>

        <Card className='border-0 shadow-lg hover:shadow-xl transition'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between mb-2'>
              <div className='w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center'>
                <DollarSign className='w-5 h-5 text-green-600' />
              </div>
            </div>
            <p className='text-sm text-gray-500'>إجمالي مبالغ الاسترداد</p>
            <p className='text-2xl font-bold text-green-700'>{stats.totalRefund.toLocaleString()} ج.م</p>
          </CardContent>
        </Card>
      </div>

      <Card className='border-0 shadow-lg'>
        <div className='p-6'>
          <div className='flex flex-col md:flex-row md:items-center gap-4 mb-6'>
            <div className='flex-1 relative'>
              <Search className='absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400' />
              <input
                type='text'
                placeholder='بحث برقم المرتجع، الشحنة، السبب، أو التاجر...'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className='w-full pr-10 pl-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:bg-white transition'
              />
            </div>
          </div>
          {loading ? (
            <div className='p-12 text-center'>
              <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto'></div>
              <p className='text-gray-500 mt-4'>جاري تحميل المرتجعات...</p>
            </div>
          ) : filteredReturns.length === 0 ? (
            <div className='p-12 text-center'>
              <RotateCcw className='w-16 h-16 text-gray-300 mx-auto mb-4' />
              <h3 className='text-lg font-semibold text-gray-900 mb-2'>لا توجد مرتجعات</h3>
              <p className='text-gray-500'>لا توجد مرتجعات حالياً</p>
            </div>
          ) : (
            <div className='overflow-x-auto'>
              <table className='w-full'>
                <thead className='bg-gray-50 border-b border-gray-100'>
                  <tr>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>رقم المرتجع</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>رقم الشحنة</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>التاجر</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>السبب</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>الحالة</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>حالة الاسترداد</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>التاريخ</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReturns.map((ret) => {
                    const StatusIcon = getStatusIcon(ret.status || '');
                    return (
                      <tr key={ret.id} className='border-b border-gray-50 hover:bg-gray-50/50 transition'>
                        <td className='py-4 px-5'>
                          <span className='font-mono font-semibold text-orange-600'>{ret.returnNumber || ret.returnId || '-'}</span>
                        </td>
                        <td className='py-4 px-5'>
                          <span className='font-semibold text-gray-900'>{ret.shipmentTrackingNumber || ret.shipmentId || '-'}</span>
                        </td>
                        <td className='py-4 px-5'>
                          <span className='text-gray-700'>{ret.merchantName || '-'}</span>
                        </td>
                        <td className='py-4 px-5'>
                          <span className='text-gray-700'>{ret.reason || '-'}</span>
                        </td>
                        <td className='py-4 px-5'>
                          <span className={'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ' + getStatusStyle(ret.status || '')}>
                            <StatusIcon className='w-3.5 h-3.5' />
                            {getStatusLabel(ret.status || '')}
                          </span>
                        </td>
                        <td className='py-4 px-5'>
                          <span className='text-sm text-gray-700'>{ret.refundStatus || '-'}</span>
                        </td>
                        <td className='py-4 px-5 text-gray-700'>
                          {ret.createdAt ? new Date(ret.createdAt).toLocaleDateString('ar-EG') : 
                           ret.requestedAt ? new Date(ret.requestedAt).toLocaleDateString('ar-EG') : '-'}
                        </td>
                        <td className='py-4 px-5'>
                          <button
                            onClick={() => { setSelectedReturn(ret); setShowViewModal(true); }}
                            className='p-2 hover:bg-orange-50 rounded-lg text-gray-500 hover:text-orange-600 transition'
                            title='عرض'
                          >
                            <Eye className='w-4 h-4' />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {showViewModal && selectedReturn && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50'>
          <Card className='w-full max-w-3xl max-h-[90vh] overflow-y-auto'>
            <div className='p-6'>
              <div className='flex items-center justify-between mb-4'>
                <h2 className='text-xl font-bold'>تفاصيل المرتجع</h2>
                <button onClick={() => setShowViewModal(false)} className='p-2 hover:bg-gray-100 rounded-lg'>
                  <X className='w-5 h-5' />
                </button>
              </div>
              <div className='space-y-4'>
                <div className='grid grid-cols-2 gap-4 pb-4 border-b'>
                  <div>
                    <p className='text-sm text-gray-500'>رقم المرتجع</p>
                    <p className='font-semibold text-orange-600 text-lg'>{selectedReturn.returnNumber || selectedReturn.returnId || '-'}</p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>رقم الشحنة</p>
                    <p className='font-semibold'>{selectedReturn.shipmentTrackingNumber || selectedReturn.shipmentId || '-'}</p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>التاجر</p>
                    <p className='font-semibold'>{selectedReturn.merchantName || '-'}</p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>الحالة</p>
                    <span className={'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ' + getStatusStyle(selectedReturn.status || '')}>
                      {getStatusLabel(selectedReturn.status || '')}
                    </span>
                  </div>
                </div>
                
                <div className='grid grid-cols-2 gap-4'>
                  <div className='col-span-2'>
                    <p className='text-sm text-gray-500'>سبب المرتجع</p>
                    <p className='font-semibold text-gray-900'>{selectedReturn.reason || '-'}</p>
                  </div>
                  {selectedReturn.reasonDetails && (
                    <div className='col-span-2'>
                      <p className='text-sm text-gray-500'>سبب المرتجع_DETAILS</p>
                      <p className='text-gray-700'>{selectedReturn.reasonDetails}</p>
                    </div>
                  )}
                  {selectedReturn.customerNotes && (
                    <div className='col-span-2'>
                      <p className='text-sm text-gray-500'>ملاحظات العميل</p>
                      <p className='text-gray-700'>{selectedReturn.customerNotes}</p>
                    </div>
                  )}
                  {selectedReturn.adminNotes && (
                    <div className='col-span-2'>
                      <p className='text-sm text-gray-500'>ملاحظات الإدارة</p>
                      <p className='text-gray-700'>{selectedReturn.adminNotes}</p>
                    </div>
                  )}
                  <div>
                    <p className='text-sm text-gray-500'>المبلغ المسترد</p>
                    <p className='font-semibold text-green-600 text-xl'>{selectedReturn.refundAmount ? selectedReturn.refundAmount.toLocaleString() + ' ج.م' : '-'}</p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>طريقة الاسترداد</p>
                    <p className='font-semibold'>{selectedReturn.refundMethod || '-'}</p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>حالة الاسترداد</p>
                    <p className='font-semibold'>{selectedReturn.refundStatus || '-'}</p>
                  </div>
                  {selectedReturn.refundDate && (
                    <div>
                      <p className='text-sm text-gray-500'>تاريخ الاسترداد</p>
                      <p className='font-semibold'>{new Date(selectedReturn.refundDate).toLocaleDateString('ar-EG')}</p>
                    </div>
                  )}
                  {selectedReturn.requestedAt && (
                    <div>
                      <p className='text-sm text-gray-500'>تاريخ الطلب</p>
                      <p className='font-semibold'>{new Date(selectedReturn.requestedAt).toLocaleDateString('ar-EG')}</p>
                    </div>
                  )}
                  {selectedReturn.approvedAt && (
                    <div>
                      <p className='text-sm text-gray-500'>تاريخ الموافقة</p>
                      <p className='font-semibold'>{new Date(selectedReturn.approvedAt).toLocaleDateString('ar-EG')}</p>
                    </div>
                  )}
                  {selectedReturn.receivedAt && (
                    <div>
                      <p className='text-sm text-gray-500'>تاريخ الاستلام</p>
                      <p className='font-semibold'>{new Date(selectedReturn.receivedAt).toLocaleDateString('ar-EG')}</p>
                    </div>
                  )}
                  {selectedReturn.completedAt && (
                    <div>
                      <p className='text-sm text-gray-500'>تاريخ الإكمال</p>
                      <p className='font-semibold'>{new Date(selectedReturn.completedAt).toLocaleDateString('ar-EG')}</p>
                    </div>
                  )}
                </div>
                
                <div className='flex gap-3 pt-4'>
                  <Button onClick={() => setShowViewModal(false)} className='flex-1 bg-orange-500 hover:bg-orange-600'>إغلاق</Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
